"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { AuthControls } from "../AuthControls";
import { SearchToggle } from "../SearchToggle";
import { MobileMenu } from "./MobileMenu";
import { HeroMenu } from "./HeroMenu";
import { Icon } from "@/components/ui/Icon";
import { getHeaderDensity } from "./headerDensity";
import { HeaderIconMenu } from "./HeaderIconMenu";
import { pickHeaderLogo, type HeaderVariantProps } from "./headerModel";
export function HeaderBrandedMegaMenu(t0) {
  const $ = _c(110);
  const {
    brand,
    items,
    iconItems,
    enabledLocales,
    showLangSwitcher,
    basePath,
    showSearch,
    textMode: t1,
    density,
    styleOverrides: t2,
    background,
    hideDesktopLogo
  } = t0;
  const textMode = t1 === undefined ? "light" : t1;
  let t3;
  if ($[0] !== t2) {
    t3 = t2 === undefined ? {} : t2;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const styleOverrides = t3;
  let t4;
  if ($[2] !== density) {
    t4 = getHeaderDensity(density);
    $[2] = density;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const d = t4;
  const textColorClass = textMode === "light" ? "text-white" : "text-foreground";
  let t5;
  if ($[4] !== brand || $[5] !== textMode) {
    t5 = pickHeaderLogo(brand, textMode);
    $[4] = brand;
    $[5] = textMode;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const displayLogo = t5;
  const [activeMegaMenu, setActiveMegaMenu] = useState(null);
  const timeoutRef = useRef(null);
  const handleMenuItemEnter = index => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setActiveMegaMenu(index);
  };
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      timeoutRef.current = setTimeout(() => {
        setActiveMegaMenu(null);
      }, 200);
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleMenuItemLeave = t6;
  let t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    t8 = [];
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  const t9 = "sticky top-0 z-50 header-surface";
  const t10 = background || "linear-gradient(135deg, var(--header-gradient-from, var(--primary)) 0%, var(--header-gradient-to, var(--primary)) 100%)";
  let t11;
  if ($[10] !== d.headerHeight || $[11] !== styleOverrides || $[12] !== t10) {
    t11 = {
      background: t10,
      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      ...styleOverrides,
      "--header-height": d.headerHeight
    };
    $[10] = d.headerHeight;
    $[11] = styleOverrides;
    $[12] = t10;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const t12 = t11 as React.CSSProperties;
  const t13 = "max-w-350 mx-auto px-4";
  const t14 = `flex items-center justify-between ${d.py}`;
  const t15 = `flex items-center ${d.logoNavGap}`;
  const t16 = basePath || "/";
  const t17 = `flex items-center ${d.logoPy} ${hideDesktopLogo ? "md:hidden" : ""}`;
  let t18;
  if ($[14] !== brand.name || $[15] !== d.logoHeight || $[16] !== d.logoImageHeight || $[17] !== d.logoImageWidth || $[18] !== displayLogo) {
    t18 = displayLogo ? <Image src={displayLogo} alt={brand.name} width={d.logoImageWidth} height={d.logoImageHeight} className={`${d.logoHeight} w-auto max-w-[55vw] md:max-w-none object-contain`} unoptimized={true} /> : <span className="text-xl font-bold">{brand.name}</span>;
    $[14] = brand.name;
    $[15] = d.logoHeight;
    $[16] = d.logoImageHeight;
    $[17] = d.logoImageWidth;
    $[18] = displayLogo;
    $[19] = t18;
  } else {
    t18 = $[19];
  }
  let t19;
  if ($[20] !== t16 || $[21] !== t17 || $[22] !== t18) {
    t19 = <a href={t16} className={t17}>{t18}</a>;
    $[20] = t16;
    $[21] = t17;
    $[22] = t18;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const t20 = `hidden md:flex items-center ${d.navGap} ${textColorClass}`;
  const t21 = items.map((item, index_0) => {
    const megaMenu = item.megaMenu;
    const hasMegaMenu = megaMenu !== null;
    const hasDropdown = !hasMegaMenu && item.children.length > 0;
    const iconNode = item.iconNode;
    const isIconOnly = Boolean(iconNode && item.iconOnly);
    return <div key={index_0} className={`flex items-center${hasDropdown ? " relative group" : ""}`} onMouseEnter={() => hasMegaMenu && handleMenuItemEnter(index_0)} onMouseLeave={handleMenuItemLeave}><Link href={item.url.startsWith("http") ? item.url : `${basePath}${item.url}`} prefetch={false} target={item.openInNewTab ? "_blank" : undefined} className={`${d.textSize} font-medium hover:opacity-80 transition inline-flex items-center gap-1.5 whitespace-nowrap`} aria-label={isIconOnly ? item.label : undefined}>{iconNode && <Icon node={iconNode} size={isIconOnly ? d.iconSize : d.iconSizeWithLabel} />}{!isIconOnly && item.label}{(hasMegaMenu || hasDropdown) && <ChevronDown className="w-3.5 h-3.5 opacity-60" />}</Link>{hasDropdown && <div className={`absolute top-full left-0 hidden group-hover:block bg-card border border-border shadow-lg rounded-lg ${d.dropdownPy} min-w-50 z-50`}>{item.children.map((child, childIndex) => <Link key={childIndex} href={`${basePath}${child.url}`} prefetch={false} target={child.openInNewTab ? "_blank" : undefined} className={`block ${d.dropdownItemPx} ${d.dropdownItemPy} ${d.textSize} text-foreground hover:bg-muted transition whitespace-nowrap`}>{child.label}</Link>)}</div>}{megaMenu && activeMegaMenu === index_0 && <HeroMenu competitions={megaMenu.competitions} teams={megaMenu.teams} basePath={basePath} textMode={textMode} isOpen={true} onMouseEnter={() => handleMenuItemEnter(index_0)} onMouseLeave={handleMenuItemLeave} />}</div>;
  });
  let t22;
  if ($[24] !== t20 || $[25] !== t21) {
    t22 = <nav className={t20}>{t21}</nav>;
    $[24] = t20;
    $[25] = t21;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== t15 || $[28] !== t19 || $[29] !== t22) {
    t23 = <div className={t15}>{t19}{t22}</div>;
    $[27] = t15;
    $[28] = t19;
    $[29] = t22;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  const t24 = `hidden md:flex items-center ${d.controlsGap} ${textColorClass}`;
  let t25;
  if ($[31] !== basePath || $[32] !== d.iconSize || $[33] !== iconItems || $[34] !== textMode) {
    t25 = iconItems.length > 0 && <HeaderIconMenu items={iconItems} basePath={basePath} variant={textMode === "light" ? "light" : "branded"} iconSize={d.iconSize} />;
    $[31] = basePath;
    $[32] = d.iconSize;
    $[33] = iconItems;
    $[34] = textMode;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] !== basePath || $[37] !== showSearch || $[38] !== textMode) {
    t26 = showSearch && <SearchToggle basePath={basePath} variant={textMode === "light" ? "light" : "branded"} />;
    $[36] = basePath;
    $[37] = showSearch;
    $[38] = textMode;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== enabledLocales || $[41] !== showLangSwitcher) {
    t27 = showLangSwitcher && <LanguageSwitcher enabledLocales={enabledLocales} />;
    $[40] = enabledLocales;
    $[41] = showLangSwitcher;
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  const t28 = textMode === "light" ? "light" : "branded";
  const t29 = styleOverrides.color as string | undefined;
  let t30;
  if ($[43] !== basePath || $[44] !== t28 || $[45] !== t29) {
    t30 = <AuthControls basePath={basePath} variant={t28} textColor={t29} />;
    $[43] = basePath;
    $[44] = t28;
    $[45] = t29;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] !== t24 || $[48] !== t25 || $[49] !== t26 || $[50] !== t27 || $[51] !== t30) {
    t31 = <div className={t24}>{t25}{t26}{t27}{t30}</div>;
    $[47] = t24;
    $[48] = t25;
    $[49] = t26;
    $[50] = t27;
    $[51] = t30;
    $[52] = t31;
  } else {
    t31 = $[52];
  }
  let t32;
  if ($[53] !== basePath || $[54] !== d.iconSize || $[55] !== iconItems || $[56] !== textMode) {
    t32 = iconItems.length > 0 && <HeaderIconMenu items={iconItems} basePath={basePath} variant={textMode === "light" ? "light" : "branded"} iconSize={d.iconSize} maxItems={2} />;
    $[53] = basePath;
    $[54] = d.iconSize;
    $[55] = iconItems;
    $[56] = textMode;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] !== basePath || $[59] !== showSearch || $[60] !== textMode) {
    t33 = showSearch && <SearchToggle basePath={basePath} variant={textMode === "light" ? "light" : "branded"} />;
    $[58] = basePath;
    $[59] = showSearch;
    $[60] = textMode;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  const t34 = textMode === "light" ? "light" : "dark";
  const t35 = textMode === "light" ? "light" : "branded";
  const t36 = styleOverrides.color as string | undefined;
  let t37;
  if ($[62] !== basePath || $[63] !== t35 || $[64] !== t36) {
    t37 = <AuthControls basePath={basePath} variant={t35} layout="inline" textColor={t36} />;
    $[62] = basePath;
    $[63] = t35;
    $[64] = t36;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  let t38;
  if ($[66] !== basePath || $[67] !== brand.name || $[68] !== displayLogo || $[69] !== enabledLocales || $[70] !== iconItems || $[71] !== items || $[72] !== showLangSwitcher || $[73] !== t34 || $[74] !== t37 || $[75] !== textMode) {
    t38 = <MobileMenu basePath={basePath} enabledLocales={enabledLocales} showLangSwitcher={showLangSwitcher} logoUrl={displayLogo} tenantName={brand.name} variant={t34} panelStyle="branded" textMode={textMode} additionalContent={t37} items={items} iconItems={iconItems} />;
    $[66] = basePath;
    $[67] = brand.name;
    $[68] = displayLogo;
    $[69] = enabledLocales;
    $[70] = iconItems;
    $[71] = items;
    $[72] = showLangSwitcher;
    $[73] = t34;
    $[74] = t37;
    $[75] = textMode;
    $[76] = t38;
  } else {
    t38 = $[76];
  }
  let t39;
  if ($[77] !== t32 || $[78] !== t33 || $[79] !== t38) {
    t39 = <div className="md:hidden flex items-center">{t32}{t33}{t38}</div>;
    $[77] = t32;
    $[78] = t33;
    $[79] = t38;
    $[80] = t39;
  } else {
    t39 = $[80];
  }
  let t40;
  if ($[81] !== t14 || $[82] !== t23 || $[83] !== t31 || $[84] !== t39) {
    t40 = <div className={t14}>{t23}{t31}{t39}</div>;
    $[81] = t14;
    $[82] = t23;
    $[83] = t31;
    $[84] = t39;
    $[85] = t40;
  } else {
    t40 = $[85];
  }
  let t41;
  if ($[86] !== basePath || $[87] !== d.iconSize || $[88] !== d.iconSizeWithLabel || $[89] !== d.mobileItemsGap || $[90] !== d.mobileItemsPy || $[91] !== d.textSize || $[92] !== items || $[93] !== textColorClass || $[94] !== textMode) {
    bb0: {
      const mobileItems = items.filter(_temp);
      if (mobileItems.length === 0) {
        t41 = null;
        break bb0;
      }
      const t42 = `md:hidden border-t ${textMode === "light" ? "border-white/10" : "border-foreground/10"}`;
      let t43;
      if ($[96] !== basePath || $[97] !== d.iconSize || $[98] !== d.iconSizeWithLabel || $[99] !== d.textSize) {
        t43 = (item_1, index_1) => {
          const iconNode_0 = item_1.iconNode;
          const isIconOnly_0 = Boolean(iconNode_0 && item_1.iconOnly);
          return <Link key={index_1} href={item_1.url.startsWith("http") ? item_1.url : `${basePath}${item_1.url}`} prefetch={false} target={item_1.openInNewTab ? "_blank" : undefined} className={`${d.textSize} font-medium hover:opacity-80 transition inline-flex items-center gap-1.5 whitespace-nowrap shrink-0`} aria-label={isIconOnly_0 ? item_1.label : undefined}>{iconNode_0 && <Icon node={iconNode_0} size={isIconOnly_0 ? d.iconSize : d.iconSizeWithLabel} />}{!isIconOnly_0 && item_1.label}</Link>;
        };
        $[96] = basePath;
        $[97] = d.iconSize;
        $[98] = d.iconSizeWithLabel;
        $[99] = d.textSize;
        $[100] = t43;
      } else {
        t43 = $[100];
      }
      const t44 = <div className={`flex items-center ${d.mobileItemsGap} px-4 ${d.mobileItemsPy} overflow-x-auto scrollbar-hide ${textColorClass}`}>{mobileItems.map(t43)}</div>;
      let t45;
      if ($[101] !== t42 || $[102] !== t44) {
        t45 = <div className={t42}>{t44}</div>;
        $[101] = t42;
        $[102] = t44;
        $[103] = t45;
      } else {
        t45 = $[103];
      }
      t41 = t45;
    }
    $[86] = basePath;
    $[87] = d.iconSize;
    $[88] = d.iconSizeWithLabel;
    $[89] = d.mobileItemsGap;
    $[90] = d.mobileItemsPy;
    $[91] = d.textSize;
    $[92] = items;
    $[93] = textColorClass;
    $[94] = textMode;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  let t42;
  if ($[104] !== t40 || $[105] !== t41) {
    t42 = <div className={t13}>{t40}{t41}</div>;
    $[104] = t40;
    $[105] = t41;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== t12 || $[108] !== t42) {
    t43 = <header className={t9} style={t12}>{t42}</header>;
    $[107] = t12;
    $[108] = t42;
    $[109] = t43;
  } else {
    t43 = $[109];
  }
  return t43;
}
function _temp(item_0) {
  return item_0.showOnMobile;
}